type SerializableValue =
  | boolean
  | number
  | string
  | Array<SerializableValue>
  | { [key: string]: SerializableValue };

export type TransformerFn = (
  ...args: any
) => (
  value: SerializableValue | undefined,
  dependencyValue?: SerializableValue
) => any;

export const toBoolean: TransformerFn = () => (value) => !!value;

export const isEqual: TransformerFn =
  (compared: SerializableValue) => (value) => {
    return Object.is(compared, value);
  };

export const isDifferent: TransformerFn =
  (compared: SerializableValue) => (value) => {
    if (!Array.isArray(compared)) compared = [compared];
    return compared.every((elmt) => elmt !== value);
  };

export const removeSpaces: TransformerFn = () => (value) =>
  typeof value === 'string' ? value.replace(/ /g, '') : value;

export const stringArrayToString: TransformerFn =
  (separator: string) => (value) =>
    value && Array.isArray(value) ? value.join(separator) : value;

export const uppercase: TransformerFn = () => (value) =>
  typeof value === 'string' ? value.toUpperCase() : value;

export const lowercase: TransformerFn = () => (value) =>
  typeof value === 'string' ? value.toLowerCase() : value;

export const replaceWith: TransformerFn =
  (patternToReplace: string, replaceBy: string, modifiers = 'gmi') =>
  (v) => {
    const rgx = new RegExp(patternToReplace, modifiers);
    return typeof v === 'string' ? v.replace(rgx, replaceBy) : v;
  };

export const multiplyDependencyBy: TransformerFn =
  (multiplyBy: number) => (value, dependencyValue) => {
    if (
      typeof dependencyValue !== 'string' &&
      typeof dependencyValue !== 'number'
    )
      return value;
    let numValue: number =
      typeof dependencyValue === 'string'
        ? parseFloat(dependencyValue)
        : dependencyValue;
    if (isNaN(numValue)) {
      numValue = 0;
    }
    return numValue * multiplyBy;
  };

export const replaceByDependencyValue: TransformerFn =
  () => (_value, dependencyValue) => {
    return dependencyValue;
  };

export const formatDate: TransformerFn =
  (requestedFormat = 'DD-MM-YYYY', modifiers = 'gmi') =>
  (v?: SerializableValue) => {
    if (!v) {
      return v;
    }

    if ((v as string).split('-')[0].length === 4) {
      v = (v as string).split('-').reverse().join('-');
    }

    const rgx = new RegExp('[0-9]{8}', modifiers);
    const rawValue = (v as string).replace(/-/g, '').replace(/ /g, '');

    if (!rgx.exec(rawValue)) {
      return v;
    }

    if (requestedFormat === 'DD-MM-YYYY') {
      return `${rawValue.slice(0, 2)}-${rawValue.slice(2, 4)}-${rawValue.slice(
        4,
        8
      )}`;
    }

    return v;
  };

export const onlyNumeric: TransformerFn = () => (v) => {
  if (typeof v !== 'string') return v;
  return v.replace(/\D/g, '');
};

export const normalizePhone: TransformerFn = () => (v) => {
  if (typeof v !== 'string') return v;

  // First remove unwanted characters but keep + for prefix detection
  let value = v.replace(/[^0-9+]/g, '');

  // Handle different prefixes and normalize to 0-prefix format
  if (value.startsWith('+31')) {
    return value.replace(/^\+31/, '0');
  } else if (value.startsWith('0031')) {
    return value.replace(/^0031/, '0');
  } else if (value.startsWith('31') && value.length > 10) {
    return value.replace(/^31/, '0');
  }

  return value;
};

export const normalizeInitials: TransformerFn = () => (v) => {
  if (!v) return v;

  const regex = /[^a-zA-Z]+/gi;
  const string = v.toString();

  let result = string
    .replace(regex, '')
    .replace(/(.{1})/g, '$1.')
    .toUpperCase();

  //  If input.length !== 2 (initial + .) then remove dot from last char
  if (result.length !== 2) {
    return result.replace(regex, '').toUpperCase().split('').join('.');
  }

  const transformedValue = string.replace(regex, '');

  const isSingleLetterWithoutDot =
    v !== transformedValue &&
    string.length === 2 &&
    transformedValue.length === 1;

  if (isSingleLetterWithoutDot) {
    return transformedValue;
  }

  if (
    v === transformedValue &&
    result.charAt(result.length - 1) === '.' &&
    v === v.toUpperCase()
  ) {
    return transformedValue;
  }

  return result;
};

const getTodaysDate = () => {
  const date = new Date(Date.now());
  let month = (date.getMonth() + 1).toString();
  if (month.length < 2) {
    month = '0' + month;
  }
  return `${date.getDate()}-${month}-${date.getFullYear()}`;
};

export const todaysDate: TransformerFn = (ts?: number) => (v) => {
  const date = new Date(ts ?? Date.now());
  const dateVal = new Date(v as string);
  const isEmpty = v === undefined || v === '';
  const isPast =
    dateVal.toString() !== 'Invalid Date' &&
    dateVal < date &&
    (v as string).length === 10;
  if (isEmpty || isPast) return getTodaysDate();
  return v;
};

// Date utils consts
const dateStep = 2;
const maxRawLength = 2;
let keyCode;
window.addEventListener('keydown', logKey);
function logKey(event) {
  keyCode = event;
}

export const numericToDate: TransformerFn =
  (
    { separator, firstSeparatorAt } = {
      separator: '-',
      firstSeparatorAt: 2,
    }
  ) =>
  (v?: SerializableValue) => {
    if (typeof v !== 'string') return v;
    const rawValue = v.replaceAll(separator, '');
    const shouldFormat = rawValue.length >= maxRawLength;
    if (shouldFormat && keyCode?.key !== 'Backspace') {
      const lastSeparatorAt = firstSeparatorAt + dateStep;
      return rawValue.split('').reduce((acc, cur, i) => {
        const shouldPrepend =
          i + 1 === firstSeparatorAt || i + 1 === lastSeparatorAt;
        return shouldPrepend ? `${acc}${cur}${separator}` : `${acc}${cur}`;
      }, '');
    }
    return v;
  };

export const normalizeDate: TransformerFn =
  (separator = '-') =>
  (v) => {
    if (typeof v !== 'string') return v;
    if (v.length > 10) v = v.substring(0, 10);

    return v
      .split('')
      .filter((val) => val === separator || /\d/.test(val))
      .join('');
  };

export const capitalize: TransformerFn = () => (v) => {
  return typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1) : v;
};

export const normalizeIBAN: TransformerFn = () => (val) => {
  if (typeof val !== 'string') return val;
  if (!val) {
    return val;
  }

  // 1 Remove special chars and white spaces
  const value = val.replace(/[^0-9a-zA-Z]+/g, '');

  // 1 First 2 chars to be strings [a-zA-Z]{2}
  // 2 Next 2 chars to be numbers \d{2}
  // 3 Next 4 chars to be strings or numbers (Bank code) \w{4}
  // 4 Next 7 chars to be numbers \d{7}

  if (value.match(/^[A-z]{2}\d{2}[A-z0-9]{4}\d{7}([A-z0-9]+){0,16}/i)) {
    const nums = value
      .substring(8)
      .replace(/(.{4})/g, '$1 ')
      .trim();

    return `${value.substring(0, 4)} ${value.substring(
      4,
      8
    )} ${nums}`.toUpperCase();
  }

  return value.toUpperCase();
};
